import React from 'react';
import { ChevronLeft, Loader2, Heart, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Quiz Header with progress
export const QuizHeader = ({ currentQuestion, totalQuestions, onBack, canGoBack }) => (
  <div className="mb-8">
    <div className="flex items-center justify-between mb-6">
      {canGoBack ? (
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-[#6B6878] hover:text-[#1A1A2E] transition-colors"
          data-testid="quiz-back-button"
        >
          <ChevronLeft className="w-5 h-5" />
          <span className="text-sm">Back</span>
        </button>
      ) : (
        <div />
      )}
      <span className="text-sm" style={{ color: '#8A8798' }}>
        {currentQuestion} of {totalQuestions}
      </span>
    </div>
    
    {/* Progress bar */}
    <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: '#EAE4DC' }}>
      <div 
        className="h-full transition-all duration-500 ease-out rounded-full"
        style={{ 
          width: `${(currentQuestion / totalQuestions) * 100}%`,
          backgroundColor: '#C9A84C'
        }}
      />
    </div>
  </div>
);

// Question Card
export const QuestionCard = ({ question, selectedAnswer, onSelectAnswer }) => (
  <div className="animate-fadeIn">
    <h2 
      className="text-xl md:text-2xl mb-8 leading-relaxed"
      style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}
    >
      {question.text}
    </h2>
    
    <div className="space-y-3">
      {question.options.map((option) => (
        <button
          key={option.id}
          onClick={() => onSelectAnswer(option.style)}
          data-testid={`quiz-option-${option.id}`}
          className={`w-full p-4 rounded-xl text-left transition-all duration-200 ${
            selectedAnswer === option.style ? 'scale-[1.02]' : 'hover:scale-[1.01]'
          }`}
          style={{
            backgroundColor: selectedAnswer === option.style 
              ? 'rgba(201, 168, 76, 0.15)' 
              : '#F7F1EE',
            border: selectedAnswer === option.style 
              ? '1px solid rgba(201, 168, 76, 0.5)' 
              : '1px solid #EAE4DC'
          }}
        >
          <div className="flex items-start gap-3">
            <div 
              className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-medium mt-0.5"
              style={{
                backgroundColor: selectedAnswer === option.style 
                  ? '#C9A84C' 
                  : '#EAE4DC',
                color: selectedAnswer === option.style 
                  ? '#FBF9F6' 
                  : '#6B6878'
              }}
            >
              {option.id.toUpperCase()}
            </div>
            <span style={{ color: '#1A1A2E' }}>{option.text}</span>
          </div>
        </button>
      ))}
    </div>
  </div>
);

// Result Screen
export const ResultScreen = ({ result, onContinue, saving }) => (
  <div className="text-center animate-fadeIn">
    <div 
      className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
      style={{ backgroundColor: `${result.color}20` }}
    >
      <Heart className="w-10 h-10" style={{ color: result.color }} />
    </div>
    
    <h2 
      className="text-2xl md:text-3xl mb-4"
      style={{ fontFamily: 'Playfair Display, serif', color: result.color }}
    >
      {result.title}
    </h2>
    
    <p 
      className="text-lg mb-8 max-w-md mx-auto leading-relaxed"
      style={{ color: '#4A4A5A' }}
    >
      {result.explanation}
    </p>
    
    <div 
      className="p-4 rounded-xl mb-8 max-w-md mx-auto"
      style={{ backgroundColor: 'rgba(201, 168, 76, 0.1)' }}
    >
      <p className="text-sm" style={{ color: '#4A4A5A' }}>
        Sage will adapt her guidance to honor your attachment style throughout your healing journey.
      </p>
    </div>
    
    <Button
      onClick={onContinue}
      disabled={saving}
      className="px-12 py-6 text-lg rounded-full font-medium"
      style={{ backgroundColor: result.color, color: '#FBF9F6' }}
      data-testid="quiz-continue-button"
    >
      {saving ? (
        <span className="flex items-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          Saving...
        </span>
      ) : (
        <span className="flex items-center gap-2">
          Continue to Your Trial
          <ArrowRight className="w-5 h-5" />
        </span>
      )}
    </Button>
  </div>
);

// Skip Option
export const SkipOption = ({ onSkip, skipping }) => (
  <div className="text-center mt-8">
    <button
      onClick={onSkip}
      disabled={skipping}
      className="text-sm hover:underline transition-colors"
      style={{ color: '#8A8798' }}
      data-testid="quiz-skip-button"
    >
      {skipping ? 'Skipping...' : 'Skip this step'}
    </button>
  </div>
);

// Loading Screen
export const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FBF9F6' }}>
    <div className="text-center">
      <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin" style={{ color: '#C9A84C' }} />
      <p style={{ color: '#6B6878' }}>Loading...</p>
    </div>
  </div>
);
